import React, { useEffect, useMemo, useState } from 'react';
import { Ship, Truck, Plane, Package } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { KpiCard } from '../../components/ui/KpiCard';
import { Badge } from '../../components/ui/Badge';
import { Table } from '../../components/ui/Table';
import { syncTimestampColumns } from '../../components/ui/SyncTimestamps';
import { useTranslation } from 'react-i18next';
import { SectionCard } from '../../components/ui/SectionCard';
import { BarChart } from '../../components/ui/BarChart';
import api from '../../services/api';
import { SHIPMENT_STATUS, fmtDate, groupByMonth } from '../../utils/dashboardUtils';
import { DashHeader, DashError, MiniEmpty } from './DashboardShared';

const MODE_META = {
  reefer: { label: 'Reefer', Icon: Ship,  bg: 'bg-sky-50',    text: 'text-sky-800'    },
  road:   { label: 'Route',  Icon: Truck, bg: 'bg-orange-50', text: 'text-orange-700' },
  air:    { label: 'Air',    Icon: Plane, bg: 'bg-sky-50',    text: 'text-sky-700'    },
};

function TransportIcon({ mode }) {
  const { Icon, bg, text } = MODE_META[mode] || MODE_META.reefer;
  return (
    <span className={`inline-flex items-center justify-center w-6 h-6 rounded ${bg} ${text}`}>
      <Icon size={13} strokeWidth={1.75} />
    </span>
  );
}

const SHIPMENT_COLS = [
  {
    key: 'containerRef',
    label: 'Réf.',
    render: (v) => <span className="font-mono text-[12px] font-medium text-primary">{v || '—'}</span>,
  },
  {
    key: 'productName',
    label: 'Produit',
    render: (v) => <span className="font-medium text-slate-700">{v}</span>,
  },
  {
    key: 'mode',
    label: 'Mode',
    render: (v) => <TransportIcon mode={v} />,
  },
  {
    key: 'route',
    label: 'Route',
    render: (_, row) => (
      <span className="text-slate-500 text-[12px]">{row.departure} → {row.arrival}</span>
    ),
  },
  {
    key: 'etd',
    label: 'ETD',
    render: (v) => <span className="text-slate-500 text-[12px]">{v}</span>,
  },
  {
    key: 'statusVariant',
    label: 'Statut',
    render: (v, row) => <Badge variant={v}>{row.statusLabel}</Badge>,
  },
];

const ORDER_COLS = [
  {
    key: 'reference',
    label: 'Réf.',
    render: (v) => <span className="font-mono text-[12px] font-medium text-primary">{v || '—'}</span>,
  },
  {
    key: 'productName',
    label: 'Produit',
    render: (v) => <span className="font-medium text-slate-700">{v || '—'}</span>,
  },
  {
    key: 'mode',
    label: 'Mode',
    render: (v) => <TransportIcon mode={v} />,
  },
  {
    key: 'originLabel',
    label: 'Origine',
    render: (v) => <span className="text-slate-500 text-[12px]">{v || '—'}</span>,
  },
  {
    key: 'etdLabel',
    label: 'ETD',
    render: (v) => <span className="text-slate-500 text-[12px]">{v || '—'}</span>,
  },
];

export default function AchatsDashboard() {
  const { t, i18n } = useTranslation();
  const timestampColumns = syncTimestampColumns(t, i18n.language);
  const { user } = useAuth();
  const [shipments, setShipments] = useState([]);
  const [orders, setOrders]       = useState([]);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState(null);

  function load() {
    setLoading(true);
    setError(null);
    Promise.all([api.get('/shipments'), api.get('/orders')])
      .then(([sRes, oRes]) => { setShipments(Array.isArray(sRes.data) ? sRes.data : (sRes.data?.data ?? [])); setOrders(oRes.data); })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }

  useEffect(() => { load(); }, []);

  const enrichedShipments = useMemo(() => {
    const orderMap = {};
    orders.forEach((o) => { orderMap[o._id] = o; });
    return shipments.map((s) => {
      const order = orderMap[s.orderId] || {};
      const mapped = SHIPMENT_STATUS[s.status] || { variant: 'info', label: s.status || '—', group: 'other' };
      return {
        ...s,
        statusVariant: mapped.variant,
        statusLabel:   mapped.label,
        statusGroup:   mapped.group,
        productName:   order.productName || '—',
        departure:     order.origin?.city || order.origin?.portCode || '—',
        arrival:       order.destination?.finalDestinationCode || order.destination?.portCode || '—',
        etd:           fmtDate(order.etd),
        mode:          s.mode || order.mode || 'reefer',
      };
    });
  }, [shipments, orders]);

  const enrichedOrders = useMemo(() => orders.map((o) => ({
    ...o,
    reference:   o.reference || o._id?.toString().slice(-6) || '—',
    originLabel: o.origin?.city || o.origin?.portCode || '—',
    etdLabel:    fmtDate(o.etd),
    mode:        o.mode || 'reefer',
  })), [orders]);

  const kpis = useMemo(() => ({
    totalOrders:  orders.length,
    inTransit:    enrichedShipments.filter((s) => s.statusGroup === 'transit').length,
    delivered:    enrichedShipments.filter((s) => s.statusGroup === 'delivered').length,
    incidents:    enrichedShipments.filter((s) => s.statusGroup === 'incident').length,
    byMode: {
      reefer: enrichedShipments.filter((s) => s.mode === 'reefer').length,
      road:   enrichedShipments.filter((s) => s.mode === 'road').length,
      air:    enrichedShipments.filter((s) => s.mode === 'air').length,
    },
  }), [orders, enrichedShipments]);

  const monthlyData    = useMemo(() => groupByMonth(shipments, 'createdAt'), [shipments]);
  const transitRows    = useMemo(() => enrichedShipments.filter((s) => s.statusGroup === 'transit').slice(0, 5), [enrichedShipments]);
  const recentOrders   = useMemo(() => enrichedOrders.slice(0, 5), [enrichedOrders]);

  const modeData = [
    { label: 'Reefer', value: kpis.byMode.reefer },
    { label: 'Route',  value: kpis.byMode.road   },
    { label: 'Air',    value: kpis.byMode.air     },
  ];

  return (
    <div className="flex flex-col gap-5">
      <DashHeader
        title="Tableau de bord — Achats"
        sub={`Bonjour ${user?.firstName} — ${orders.length} commande${orders.length !== 1 ? 's' : ''} enregistrée${orders.length !== 1 ? 's' : ''}`}
        onRefresh={load}
        loading={loading}
      />

      {error && <DashError message={error} onRetry={load} />}

      <div className="grid grid-cols-4 gap-4">
        <KpiCard
          label="Total commandes"
          value={loading ? '—' : kpis.totalOrders}
          icon={Package}
        />
        <KpiCard
          label="En transit"
          value={loading ? '—' : kpis.inTransit}
          icon={Ship}
          sub={!loading && enrichedShipments.length ? `${Math.round((kpis.inTransit / enrichedShipments.length) * 100)} % des expéditions` : undefined}
        />
        <KpiCard
          label="Livrées"
          value={loading ? '—' : kpis.delivered}
          sub={!loading && enrichedShipments.length ? `${Math.round((kpis.delivered / enrichedShipments.length) * 100)} % des expéditions` : undefined}
        />
        <KpiCard
          label="Incidents"
          value={loading ? '—' : kpis.incidents}
          trend={kpis.incidents > 0 ? 'down' : undefined}
          trendLabel={kpis.incidents > 0 ? `${kpis.incidents} à traiter` : 'Aucun incident'}
        />
      </div>

      <div className="grid grid-cols-3 gap-4">
        <SectionCard title="Expéditions par mois" className="col-span-2">
          {!loading && <BarChart data={monthlyData} />}
          {loading && <div className="h-[68px] bg-slate-100 rounded animate-pulse" />}
        </SectionCard>
        <SectionCard title="Répartition par mode">
          {loading
            ? <div className="space-y-2.5">{[1, 2, 3].map((i) => <div key={i} className="h-4 bg-slate-100 rounded animate-pulse" />)}</div>
            : (
              <div className="space-y-2.5">
                {modeData.map((m) => {
                  const total = modeData.reduce((s, x) => s + x.value, 0);
                  const pct   = total > 0 ? Math.round((m.value / total) * 100) : 0;
                  const meta  = MODE_META[m.label.toLowerCase()] || MODE_META.reefer;
                  const Icon  = meta.Icon;
                  return (
                    <div key={m.label} className="flex items-center gap-2">
                      <span className={`inline-flex items-center justify-center w-6 h-6 rounded shrink-0 ${meta.bg} ${meta.text}`}>
                        <Icon size={12} strokeWidth={1.75} />
                      </span>
                      <span className="text-[12px] text-slate-600 w-12 shrink-0">{m.label}</span>
                      <div className="flex-1 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                        <div
                          className="h-full rounded-full bg-slate-400 transition-all duration-500"
                          style={{ width: `${pct}%` }}
                        />
                      </div>
                      <span className="text-[12px] font-semibold text-slate-700 tabular-nums w-6 text-right">
                        {m.value}
                      </span>
                    </div>
                  );
                })}
              </div>
            )
          }
        </SectionCard>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <SectionCard
          title="Expéditions en transit"
          action={
            <span className="text-[11px] font-semibold text-slate-500 bg-slate-100 px-2 py-0.5 rounded-full">
              {transitRows.length}
            </span>
          }
        >
          {transitRows.length > 0
            ? <Table columns={[...SHIPMENT_COLS, ...timestampColumns]} rows={transitRows} rowKey="_id" />
            : <MiniEmpty label="Aucune expédition en transit" />
          }
        </SectionCard>

        <SectionCard
          title="Commandes récentes"
          action={
            <span className="text-[11px] font-semibold text-slate-500 bg-slate-100 px-2 py-0.5 rounded-full">
              {recentOrders.length}
            </span>
          }
        >
          {recentOrders.length > 0
            ? <Table columns={[...ORDER_COLS, ...timestampColumns]} rows={recentOrders} rowKey="_id" />
            : <MiniEmpty label="Aucune commande enregistrée" />
          }
        </SectionCard>
      </div>
    </div>
  );
}
